import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ImageCard from "../components/ImageCard";
import Lightbox from "../components/Lightbox";
import { galleryCategories } from "../data/galleryData";

const GalleryCategoryPage = () => {
    const { categoryId } = useParams();
    const navigate = useNavigate();
    const [category, setCategory] = useState(null);
    const [displayedImages, setDisplayedImages] = useState([]);
    const [hasMore, setHasMore] = useState(true);
    const [lightboxImage, setLightboxImage] = useState(null);
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const observer = useRef();

    useEffect(() => {
        const foundCategory = galleryCategories.find(cat => cat.id === categoryId);
        if (!foundCategory) {
            navigate('/gallery');
            return;
        }
        setCategory(foundCategory);
        setDisplayedImages(foundCategory.images.slice(0, 12));
        setHasMore(foundCategory.images.length > 12);
    }, [categoryId, navigate]);

    const loadMoreImages = useCallback(() => {
        if (!hasMore || isLoading || !category) return;
        setIsLoading(true);
        
        setTimeout(() => {
            const currentLength = displayedImages.length;
            const nextImages = category.images.slice(currentLength, currentLength + 12);
            setDisplayedImages(prev => [...prev, ...nextImages]);
            if (currentLength + nextImages.length >= category.images.length) {
                setHasMore(false);
            }
            setIsLoading(false);
        }, 300);
    }, [hasMore, isLoading, category, displayedImages.length]);

    const lastImageRef = useCallback(
        (node) => {
            if (isLoading) return;
            if (observer.current) observer.current.disconnect();
            observer.current = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting && hasMore) {
                    loadMoreImages();
                }
            });
            if (node) observer.current.observe(node);
        },
        [hasMore, isLoading, loadMoreImages]
    );

    const handleImageClick = (image) => {
        setLightboxImage(image);
        setIsLightboxOpen(true);
    };

    const closeLightbox = () => {
        setIsLightboxOpen(false);
        setLightboxImage(null);
    };

    if (!category) {
        return (
            <div className="bg-primary min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-800"></div>
            </div>
        );
    }

    return (
        <div className="bg-primary min-h-screen">
            <section className="px-4 py-12 max-w-7xl mx-auto">
                {/* Back button */}
                <button
                    onClick={() => navigate('/gallery')}
                    className="mb-6 flex items-center text-red-800 hover:text-red-600 transition-colors"
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to Gallery
                </button>

                {/* Category header */}
                <h2 className="text-4xl font-medium text-center text-red-800 mb-2">
                    {category.name}
                </h2>
                <p className="text-lg text-center text-gray-700 mb-10">
                    {category.description}
                </p>
                <div className="h-px w-full mb-10 bg-orange-700"></div>

                {/* Images grid */}
                <div className="columns-1 sm:columns-2 md:columns-3 lg:columns-4 gap-6 space-y-6">
                    {displayedImages.map((img, idx) => {
                        const isLastImage = idx === displayedImages.length - 1;
                        return (
                            <div 
                                ref={isLastImage ? lastImageRef : null} 
                                key={`${img.src}-${idx}`}
                                className="break-inside-avoid mb-6"
                            >
                                <ImageCard 
                                    src={img.src}
                                    title={img.title}
                                    description={img.description}
                                    onClick={() => handleImageClick(img)}
                                    className="cursor-pointer hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 w-full"
                                />
                            </div>
                        );
                    })}
                </div>

                {/* Loading indicator */}
                {isLoading && (
                    <div className="flex justify-center mt-8">
                        <div className="flex items-center space-x-2">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-800"></div>
                            <span className="text-red-800 font-medium">Loading more images...</span>
                        </div>
                    </div>
                )}

                {/* Load more button */}
                {hasMore && !isLoading && (
                    <div className="flex justify-center mt-8">
                        <button
                            onClick={loadMoreImages}
                            className="px-6 py-3 bg-red-800 text-white rounded-full hover:bg-red-700 transition-colors duration-300 font-medium"
                        >
                            Load More Images
                        </button>
                    </div>
                )}

                {!hasMore && displayedImages.length > 0 && (
                    <p className="text-center mt-10 text-gray-500">
                        No more images to show.
                    </p>
                )}
            </section>

            {/* Lightbox Modal */}
            {isLightboxOpen && lightboxImage && (
                <Lightbox
                    image={lightboxImage}
                    onClose={closeLightbox}
                />
            )}
        </div>
    );
};

export default GalleryCategoryPage;
